import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { CognitoService } from 'src/app/auth/services/cognito.service';
import { UserRoles } from '../enums/roles-enum';
import { LOGIN_ID_KEY, readReturnUrl } from '../utils/session-tabs';

/**
 * Devuelve a su página a las pestañas del operador que quedaron en el login.
 *
 * Caso típico: el operador tiene varias pestañas, cierra el portátil y la
 * sesión vence. Cada pestaña cae en el login con `?volver=` (ver
 * `session-tabs.ts`). Cuando entra en una, esta escucha el login por el evento
 * `storage` y vuelve a la página anotada. También se revisa al volver al
 * foreground: en móvil una pestaña congelada puede perderse el evento.
 *
 * Sólo operadores: es el ajuste que se pidió para ellos, y para un cliente
 * saltar solo desde el login a otra pantalla sería desconcertante.
 */
@Injectable({ providedIn: 'root' })
export class SessionResumeService implements OnDestroy {
  private started = false;
  private enCurso = false;

  constructor(
    private cognito: CognitoService,
    private router: Router,
  ) {}

  /** Idempotente: se puede llamar en cada arranque sin duplicar listeners. */
  start(): void {
    if (this.started) return;
    this.started = true;
    window.addEventListener('storage', this.onStorage);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  stop(): void {
    window.removeEventListener('storage', this.onStorage);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.started = false;
  }

  private onStorage = (event: StorageEvent): void => {
    // `newValue` null es un `signOut()` en otra pestaña, no un login.
    if (event.key === LOGIN_ID_KEY && event.newValue) {
      void this.retomar();
    }
  };

  private onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') {
      void this.retomar();
    }
  };

  /**
   * Si esta pestaña está en el login y ya hay sesión de operador, vuelve.
   *
   * Recarga en lugar de navegar: la pestaña viene de una sesión cortada y
   * puede arrastrar estado de ella (store, socket, pollings). Recargar la deja
   * igual que si el operador hubiera abierto esa página a mano.
   */
  private async retomar(): Promise<void> {
    if (this.enCurso) return;
    if (!window.location.pathname.startsWith('/auth/login')) return;

    this.enCurso = true;
    try {
      this.cognito.clearCache();
      const session = await this.cognito.getSession();
      if (!session) return;

      const user = await this.cognito.getUser();
      if (user?.attributes?.['custom:user-group'] !== UserRoles.aguacate_operator) {
        return;
      }

      // Pudo haber navegado mientras se preguntaba.
      if (!window.location.pathname.startsWith('/auth/login')) return;

      window.location.assign(readReturnUrl() ?? '/wallet');
    } catch {
      /* sin sesión legible: la pestaña se queda en el login */
    } finally {
      this.enCurso = false;
    }
  }

  ngOnDestroy(): void {
    this.stop();
  }
}
